import {
  nextDensity,
  nextTheme,
  Pane,
  Plate,
  Rail,
  Skeleton,
  useDensity,
  useTheme,
} from "@micropod/ui";
import { type CSSProperties, useCallback, useEffect, useState } from "react";
import { CreateWorkspace } from "./auth/CreateWorkspace";
import { SignIn } from "./auth/SignIn";
import { Inbox } from "./inbox/Inbox";
import { getToken, setUnauthorizedHandler } from "./lib/api";
import {
  ApiUnreachable,
  getSession,
  listOrganizations,
  type Session,
  setActiveOrganization,
  signOut,
} from "./lib/api/auth";
import { Wizard } from "./onboarding/Wizard";
import { CommandPalette, type PaletteAction } from "./ui/CommandPalette";
import { Icon } from "./ui/Icon";

/**
 * `unreachable` is the state this gate was missing.
 *
 * Without it a signed-in user whose network hiccupped was shown the sign-in screen, which
 * says something false — they are signed in, we just cannot ask. See lib/api/auth.ts.
 */
type Gate = "checking" | "signed_out" | "unreachable" | "no_workspace" | "ready";
type View = "inbox" | "setup";

export function App() {
  const [gate, setGate] = useState<Gate>("checking");
  const [session, setSession] = useState<Session | null>(null);
  const [view, setView] = useState<View>("inbox");
  const [theme, setTheme] = useTheme();
  const [density, setDensity] = useDensity();

  const check = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setGate("signed_out");
      return;
    }
    let s: Session | null;
    try {
      s = await getSession(token);
    } catch (err) {
      if (err instanceof ApiUnreachable) {
        // The token is kept. Nothing about it has been disproved.
        setGate("unreachable");
        return;
      }
      throw err;
    }
    if (!s) {
      setGate("signed_out");
      return;
    }

    // A fresh sign-in starts with no active organization, so a returning tenant would be
    // asked to create a second workspace. If they already have one, select it for them —
    // choosing is only a question when there is genuinely a choice to make.
    if (!s.activeOrganizationId) {
      const orgs = await listOrganizations(token).catch(() => []);
      if (orgs.length > 0 && orgs[0]) {
        await setActiveOrganization(token, orgs[0].id);
        s = (await getSession(token)) ?? s;
      }
    }

    setSession(s);
    // Without an active organization there is no tenant and the API refuses every call,
    // so naming the business is part of signing up rather than a setting.
    setGate(s.activeOrganizationId ? "ready" : "no_workspace");
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  /**
   * A bearer token outlives the tab it was minted in. When any tRPC call meets a 401 the
   * gate hears about it once, here, instead of every screen inventing its own handling.
   */
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setSession(null);
      setGate("signed_out");
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  /** Sign out on the server too, then locally. One place, two call sites. */
  const leave = useCallback(async () => {
    await signOut(getToken());
    setSession(null);
    setGate("signed_out");
  }, []);

  const actions: PaletteAction[] =
    gate === "ready"
      ? [
          {
            id: "inbox",
            label: "Go to Inbox",
            group: "Navigate",
            icon: "inbox",
            hint: "g i",
            run: () => setView("inbox"),
          },
          {
            id: "channels",
            label: "Go to Channels",
            group: "Navigate",
            icon: "channels",
            hint: "g c",
            run: () => setView("setup"),
          },
          {
            id: "density",
            // Density lives here rather than in the rail: it is a preference, not a place,
            // and the rail is already carrying as many controls as 56px should.
            label: density === "compact" ? "Use comfortable rows" : "Use compact rows",
            group: "View",
            icon: "rows",
            run: () => setDensity(nextDensity(density)),
          },
          {
            id: "theme",
            label: `Theme: ${theme} — switch`,
            group: "View",
            icon: "theme",
            run: () => setTheme(nextTheme(theme)),
          },
          {
            id: "signout",
            label: "Sign out",
            group: "Account",
            icon: "signout",
            run: () => {
              void leave();
            },
          },
        ]
      : [];

  // A rail holding only a logo is neither navigation nor decoration, so signed-out screens
  // drop it and the single plate fills the frame on its own.
  const signedIn = gate === "ready";

  return (
    <>
      {gate === "ready" && <CommandPalette actions={actions} />}
      {signedIn && (
        <Rail>
          <div className="mark" aria-hidden="true">
            M
          </div>
          {gate === "ready" && (
            /* The active indicator is one element that slides between items rather than a
               background that blinks from one to the next — the smallest amount of motion
               that makes navigation feel continuous, and transform-only. docs/08 §7.3. */
            <div
              className="railnav"
              style={{ "--nav-index": view === "inbox" ? 0 : 1 } as CSSProperties}
            >
              <span className="railnav-mark" aria-hidden="true" />
              <button
                type="button"
                className={`railbtn nav${view === "inbox" ? " on" : ""}`}
                title="Inbox"
                aria-label="Inbox"
                aria-current={view === "inbox" ? "page" : undefined}
                onClick={() => setView("inbox")}
              >
                <Icon name="inbox" />
              </button>
              <button
                type="button"
                className={`railbtn nav${view === "setup" ? " on" : ""}`}
                title="Channels"
                aria-label="Channels"
                aria-current={view === "setup" ? "page" : undefined}
                onClick={() => setView("setup")}
              >
                <Icon name="channels" />
              </button>
            </div>
          )}
          <div className="spacer" />
          {/* Dark mode is not optional — agents work evening shifts. docs/03 §4. */}
          <button
            type="button"
            className="railbtn nav"
            title={`Theme: ${theme}`}
            aria-label={`Theme: ${theme}. Click to change.`}
            onClick={() => setTheme(nextTheme(theme))}
          >
            <Icon name="theme" />
          </button>
          {gate === "ready" && (
            <button
              type="button"
              className="railbtn"
              title={session?.user.email ?? "Sign out"}
              aria-label="Sign out"
              onClick={() => {
                void leave();
              }}
            >
              <Icon name="signout" />
            </button>
          )}
        </Rail>
      )}

      {/* A single-pane screen, the same shape as Channel health in §11: one plate filling
          the frame, one pane, content centred in it. No nested surface — a second rounded
          box with a second shadow is the tell §2 names. */}
      {!signedIn && (
        <Plate>
          <Pane kind="auth">
            <div className="auth-center">
              <div className="brand">
                <span className="brand-mark" aria-hidden="true">
                  M
                </span>
                <span className="brand-name">Micropod</span>
              </div>

              {gate === "checking" && (
                <div className="auth-form">
                  <Skeleton width="180px" lines={2} />
                </div>
              )}
              {gate === "signed_out" && <SignIn onDone={check} />}
              {gate === "no_workspace" && <CreateWorkspace onDone={check} />}

              {/* An outage says it is an outage. The previous behaviour rendered the
                  sign-in form here, which told the person something false about their
                  own account and lost their place for a reason that was never theirs. */}
              {gate === "unreachable" && (
                <div className="auth-form">
                  <h1 className="signin-title">We could not reach Micropod</h1>
                  <p className="signin-sub">
                    You are still signed in. This is usually a brief network problem, and nothing
                    you were working on has been lost.
                  </p>
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() => {
                      setGate("checking");
                      void check();
                    }}
                  >
                    Try again
                  </button>
                  <button type="button" className="linkbtn" onClick={() => void leave()}>
                    Sign out instead
                  </button>
                </div>
              )}
            </div>

            {/* Signed-out screens carry no rail, so the theme control travels with them. */}
            <footer className="pane-foot">
              <button type="button" className="ghostbtn" onClick={() => setTheme(nextTheme(theme))}>
                <Icon name="theme" size={14} />
                Theme: {theme}
              </button>
            </footer>
          </Pane>
        </Plate>
      )}

      {gate === "ready" && <Plate>{view === "inbox" ? <Inbox /> : <Wizard />}</Plate>}
    </>
  );
}
